import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Fragment, lazy, Suspense, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import Checkbox from '@atlaskit/checkbox';
import { FlagGroup } from '@atlaskit/flag';
import InfoIcon from '@atlaskit/icon/utility/migration/information--info';
import { RovoIcon } from '@atlaskit/legacy-custom-icons';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives';
import AtlassianIntelligenceIcon from '../../atlassian-intelligence-icon';
import { AiBrand, ATLASSIAN_INTELLIGENCE_TRUST_LINK, ATLASSIAN_ROVO_LINK, CardTemplate, FEEDBACK_CONTEXT_CF, FEEDBACK_ENTRYPOINT_ID_SAIN } from '../../constants';
import { messages } from '../../messages';
import { DefinitionFooter } from '../../v2/definitions/footer/definition-footer';
import { AIAnswerWhoFooter } from '../ai-answer-body/ai-answer-result';
import { useAIAnswerContext } from '../ai-answer-context';
import { NLPSearchErrorState, NLPSearchResultFormat } from '../types';
import { FeedbackButtons } from './feedback-buttons';
import { FollowUpSuggestionFooter } from './followup-suggestion-footer';
import { AIAnswerFeedbackWrapper, AIAnswerFooterInformationContentVary, AIAnswerFooterInformationContentVaryLinkText, AIAnswerFooterPoweredBy, AIAnswerFooterPoweredByAIWrapper, AIAnswerFooterWrapper, AIAnswerReportDescriptionWrapper } from './styled';
var AkFeedbackCollector = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-collector" */'@atlaskit/feedback-collector').then(function (module) {
    return {
      default: module.default
    };
  });
});
var AkFeedbackFlag = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-flag" */'@atlaskit/feedback-collector').then(function (_ref) {
    var FeedbackFlag = _ref.FeedbackFlag;
    return {
      default: FeedbackFlag
    };
  });
});
export var SmartAnswerFooterSection = function SmartAnswerFooterSection() {
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    cardTemplate = _useAIAnswerContext.cardTemplate,
    brand = _useAIAnswerContext.brand;
  return /*#__PURE__*/React.createElement(AIAnswerFooterWrapper, null, /*#__PURE__*/React.createElement(AIAnswerFooterInformationContentVary, null, brand === AiBrand.ROVO && fg('platform_search_ai_rovo_beta_label_off') ? null : /*#__PURE__*/React.createElement(Lozenge, null, formatMessage(messages.ai_answer_component_beta_status_label)), /*#__PURE__*/React.createElement(InfoIcon, {
    label: "",
    color: "var(--ds-icon-subtle, #626F86)",
    LEGACY_size: "small"
  }), /*#__PURE__*/React.createElement(AIAnswerFooterInformationContentVaryLinkText, null, /*#__PURE__*/React.createElement(Link, {
    rel: "noopener noreferrer",
    appearance: "subtle",
    href: ATLASSIAN_INTELLIGENCE_TRUST_LINK
  }, formatMessage(cardTemplate === CardTemplate.RIGHT_PANEL ? messages.ai_answer_dialog_footer_content_quality_short : messages.ai_answer_dialog_footer_content_quality_long)))), /*#__PURE__*/React.createElement(AIAnswerFooterPoweredBy, null, brand === AiBrand.ROVO ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
    rel: "noopener noreferrer",
    appearance: "subtle",
    href: ATLASSIAN_ROVO_LINK
  }, "Rovo"), /*#__PURE__*/React.createElement(RovoIcon, {
    label: "",
    primaryColor: "var(--ds-icon-subtle, #626F86)"
  })) : /*#__PURE__*/React.createElement(AIAnswerFooterPoweredByAIWrapper, null, formatMessage(messages.ai_answer_dialog_loaded_footer), /*#__PURE__*/React.createElement(AtlassianIntelligenceIcon, {
    greyed: true,
    size: 'large'
  }))));
};
export var SearchPageSmartAnswerFooter = function SearchPageSmartAnswerFooter(_ref2) {
  var _ref2$showFollowups = _ref2.showFollowups,
    showFollowups = _ref2$showFollowups === void 0 ? true : _ref2$showFollowups;
  var _useIntl2 = useIntl(),
    formatMessage = _useIntl2.formatMessage,
    locale = _useIntl2.locale;
  var _useAIAnswerContext2 = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext2.commonAttributes,
    _useAIAnswerContext2$ = _useAIAnswerContext2.userDetails,
    name = _useAIAnswerContext2$.name,
    id = _useAIAnswerContext2$.id,
    readingAidsOptions = _useAIAnswerContext2.readingAidsOptions,
    loading = _useAIAnswerContext2.loading,
    isAdminHubAIEnabled = _useAIAnswerContext2.isAdminHubAIEnabled;
  var errorState = commonAttributes.errorState;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showFeedbackModal = _useState2[0],
    setFeedbackModal = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    displayFlag = _useState4[0],
    setDisplayFlag = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    hasConsent = _useState6[0],
    setConsent = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    topicsNotRelevant = _useState8[0],
    setTopicsNotRelevant = _useState8[1];
  var feedbackContextObject = {
    source: commonAttributes.source,
    queryHash: commonAttributes.queryHash,
    cloudId: commonAttributes.cloudId,
    prompt: hasConsent ? commonAttributes.query : undefined,
    answer: hasConsent ? commonAttributes.answerString : undefined,
    documentId: readingAidsOptions === null || readingAidsOptions === void 0 ? void 0 : readingAidsOptions.contentARI,
    errorState: errorState,
    topicsNotRelevant: topicsNotRelevant ? true : undefined
  };
  var feedbackContext = Object.entries(feedbackContextObject).map(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
      key = _ref4[0],
      value = _ref4[1];
    return "".concat(key, ": ").concat(value);
  }).join(', ');
  var toggleConsentCheckbox = useCallback(function () {
    setConsent(!hasConsent);
  }, [hasConsent]);
  var toggleTopicsNotRelevantCheckbox = useCallback(function () {
    setTopicsNotRelevant(!topicsNotRelevant);
  }, [topicsNotRelevant]);
  var topicsEnabled = isAdminHubAIEnabled && fg('ai_topics_surface_person_knowledge_card');
  return /*#__PURE__*/React.createElement(Fragment, null, showFollowups && /*#__PURE__*/React.createElement(FollowUpSuggestionFooter, null), !loading && /*#__PURE__*/React.createElement(AIAnswerFeedbackWrapper, {
    isCompact: true
  }, /*#__PURE__*/React.createElement(FeedbackButtons, {
    setDisplayFlag: setDisplayFlag,
    setFeedbackModal: setFeedbackModal
  })), /*#__PURE__*/React.createElement(SmartAnswerFooterSection, null), showFeedbackModal ? /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(AkFeedbackCollector, {
    locale: locale,
    onClose: function onClose() {
      return setFeedbackModal(false);
    },
    onSubmit: function onSubmit() {
      setDisplayFlag(true);
    },
    customContent: /*#__PURE__*/React.createElement(Stack, {
      space: "space.050"
    }, /*#__PURE__*/React.createElement(Checkbox, {
      label: formatMessage(messages.ai_answer_component_report_consent_checkbox_label),
      onChange: toggleConsentCheckbox,
      isChecked: hasConsent
    }), topicsEnabled && /*#__PURE__*/React.createElement(Checkbox, {
      label: formatMessage(messages.ai_answer_component_topics_not_relevant_checkbox_label),
      onChange: toggleTopicsNotRelevantCheckbox,
      isChecked: topicsNotRelevant
    })),
    atlassianAccountId: id,
    name: name,
    entrypointId: FEEDBACK_ENTRYPOINT_ID_SAIN,
    additionalFields: [{
      id: FEEDBACK_CONTEXT_CF,
      value: feedbackContext
    }],
    feedbackTitle: formatMessage(messages.ai_answer_component_report_modal_title),
    feedbackTitleDetails: /*#__PURE__*/React.createElement(AIAnswerReportDescriptionWrapper, null, formatMessage(messages.ai_answer_component_report_summary_label)),
    feedbackGroupLabels: {
      empty: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_empty_option_share_label)
      },
      question: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_relevance_option_label)
      },
      comment: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_inaccurate_option_label)
      },
      bug: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_offensive_option_label)
      },
      suggestion: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_other_option_label)
      }
    },
    summaryPlaceholder: formatMessage(messages.ai_answer_component_summary_placeholder_label)
  })) : null, displayFlag && /*#__PURE__*/React.createElement(FlagGroup, {
    onDismissed: function onDismissed() {
      return setDisplayFlag(false);
    }
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(AkFeedbackFlag, {
    locale: locale,
    description: formatMessage(messages.ai_answer_component_feedback_flag_description)
  }))));
};
var AIAnswerDialogFooter = function AIAnswerDialogFooter() {
  var _useIntl3 = useIntl(),
    formatMessage = _useIntl3.formatMessage,
    locale = _useIntl3.locale;
  var _useAIAnswerContext3 = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext3.commonAttributes,
    isAnswered = _useAIAnswerContext3.isAnswered,
    loading = _useAIAnswerContext3.loading,
    _useAIAnswerContext3$ = _useAIAnswerContext3.userDetails,
    name = _useAIAnswerContext3$.name,
    id = _useAIAnswerContext3$.id,
    isAdminHubAIEnabled = _useAIAnswerContext3.isAdminHubAIEnabled;
  var answerFormat = commonAttributes.answerFormat,
    errorState = commonAttributes.errorState;
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    showFeedbackModal = _useState10[0],
    setFeedbackModal = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    displayFlag = _useState12[0],
    setDisplayFlag = _useState12[1];
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    hasConsent = _useState14[0],
    setConsent = _useState14[1];
  var _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    topicsNotRelevant = _useState16[0],
    setTopicsNotRelevant = _useState16[1];
  var feedbackContextObject = {
    searchSessionId: commonAttributes.searchSessionId,
    source: commonAttributes.source,
    queryHash: commonAttributes.queryHash,
    cloudId: commonAttributes.cloudId,
    prompt: hasConsent ? commonAttributes.query : undefined,
    answer: hasConsent ? commonAttributes.answerString : undefined,
    errorState: commonAttributes.errorState,
    topicsNotRelevant: topicsNotRelevant ? true : undefined
  };
  var feedbackContext = Object.entries(feedbackContextObject).map(function (_ref5) {
    var _ref6 = _slicedToArray(_ref5, 2),
      key = _ref6[0],
      value = _ref6[1];
    return "".concat(key, ": ").concat(value);
  }).join(', ');
  var toggleConsentCheckbox = useCallback(function () {
    setConsent(!hasConsent);
  }, [hasConsent]);
  var toggleTopicsNotRelevantCheckbox = useCallback(function () {
    setTopicsNotRelevant(!topicsNotRelevant);
  }, [topicsNotRelevant]);
  var hasError = loading !== undefined && !!errorState && errorState !== NLPSearchErrorState.Default;
  var topicsEnabled = isAdminHubAIEnabled && fg('ai_topics_surface_person_knowledge_card');
  var noAnswer = !isAnswered && hasError;
  return /*#__PURE__*/React.createElement(Fragment, null, answerFormat === NLPSearchResultFormat.JSON && /*#__PURE__*/React.createElement(AIAnswerWhoFooter, null), /*#__PURE__*/React.createElement(FollowUpSuggestionFooter, null), noAnswer ? null : /*#__PURE__*/React.createElement(AIAnswerFeedbackWrapper, {
    isCompact: true
  }, /*#__PURE__*/React.createElement(FeedbackButtons, {
    setDisplayFlag: setDisplayFlag,
    setFeedbackModal: setFeedbackModal
  })), /*#__PURE__*/React.createElement(SmartAnswerFooterSection, null), showFeedbackModal ? /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(AkFeedbackCollector, {
    locale: locale,
    onClose: function onClose() {
      return setFeedbackModal(false);
    },
    onSubmit: function onSubmit() {
      setDisplayFlag(true);
    },
    customContent: /*#__PURE__*/React.createElement(Stack, {
      space: "space.050"
    }, /*#__PURE__*/React.createElement(Checkbox, {
      label: formatMessage(messages.ai_answer_component_report_consent_checkbox_label),
      onChange: toggleConsentCheckbox,
      isChecked: hasConsent
    }), topicsEnabled && /*#__PURE__*/React.createElement(Checkbox, {
      label: formatMessage(messages.ai_answer_component_topics_not_relevant_checkbox_label),
      onChange: toggleTopicsNotRelevantCheckbox,
      isChecked: topicsNotRelevant
    })),
    atlassianAccountId: id,
    name: name,
    entrypointId: FEEDBACK_ENTRYPOINT_ID_SAIN,
    additionalFields: [{
      id: FEEDBACK_CONTEXT_CF,
      value: feedbackContext
    }],
    feedbackTitle: formatMessage(messages.ai_answer_component_report_modal_title),
    feedbackTitleDetails: /*#__PURE__*/React.createElement(AIAnswerReportDescriptionWrapper, null, formatMessage(messages.ai_answer_component_report_summary_label)),
    feedbackGroupLabels: {
      empty: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_empty_option_label)
      },
      question: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_relevance_option_label)
      },
      comment: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_inaccurate_option_label)
      },
      bug: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_offensive_option_label)
      },
      suggestion: {
        fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
        selectOptionLabel: formatMessage(messages.ai_answer_component_report_other_option_label)
      }
    },
    summaryPlaceholder: formatMessage(messages.ai_answer_component_summary_placeholder_label)
  })) : null, displayFlag && /*#__PURE__*/React.createElement(FlagGroup, {
    onDismissed: function onDismissed() {
      return setDisplayFlag(false);
    }
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(AkFeedbackFlag, {
    locale: locale,
    description: formatMessage(messages.ai_answer_component_feedback_flag_description)
  }))));
};
export var AIAnswerFooter = function AIAnswerFooter() {
  var _useAIAnswerContext4 = useAIAnswerContext(),
    isReadingAids = _useAIAnswerContext4.isReadingAids,
    cardTemplate = _useAIAnswerContext4.cardTemplate;
  switch (cardTemplate) {
    case CardTemplate.SMART_ANSWER:
    case CardTemplate.RIGHT_PANEL:
      return /*#__PURE__*/React.createElement(SearchPageSmartAnswerFooter, null);
    default:
      return isReadingAids ? /*#__PURE__*/React.createElement(DefinitionFooter, null) : /*#__PURE__*/React.createElement(AIAnswerDialogFooter, null);
  }
};